/**
 * MinistrareIA - Register Page
 * Clean, centered card design (white-label ready)
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { colors } = useTheme();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      await register(formData.email, formData.password, formData.name);
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
      padding: '24px',
    },
    card: {
      width: '100%',
      maxWidth: '420px',
      backgroundColor: colors.bgPrimary,
      borderRadius: '16px',
      border: `1px solid ${colors.borderColor}`,
      padding: '40px 36px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    },
    logoText: {
      fontSize: '22px',
      fontWeight: 700,
      color: colors.accentPrimary,
      textAlign: 'center',
      marginBottom: '32px',
    },
    formHeader: {
      marginBottom: '28px',
      textAlign: 'center',
    },
    formTitle: {
      fontSize: '24px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '6px',
    },
    formSubtitle: {
      fontSize: '14px',
      color: colors.textSecondary,
    },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      color: '#ef4444',
      fontSize: '14px',
      marginBottom: '20px',
      textAlign: 'center',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '18px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    inputIcon: {
      position: 'absolute',
      left: '14px',
      color: colors.textTertiary,
      pointerEvents: 'none',
      transition: 'color 0.2s',
    },
    input: {
      width: '100%',
      padding: '14px 14px 14px 46px',
      backgroundColor: colors.bgSecondary,
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '15px',
      color: colors.textPrimary,
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box',
    },
    inputFocused: {
      borderColor: colors.accentPrimary,
      boxShadow: `0 0 0 3px ${colors.accentPrimary}20`,
    },
    inputAction: {
      position: 'absolute',
      right: '12px',
      padding: '6px',
      border: 'none',
      background: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    passwordRow: {
      display: 'grid',
      gridTemplateColumns: typeof window !== 'undefined' && window.innerWidth < 900 ? '1fr' : '1fr 1fr',
      gap: '12px',
    },
    submitBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 24px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background-color 0.2s, transform 0.1s',
      marginTop: '4px',
    },
    submitBtnDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
    footer: {
      marginTop: '24px',
      textAlign: 'center',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    link: {
      color: colors.accentPrimary,
      textDecoration: 'none',
      fontWeight: 500,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoText}>MinistrareIA</div>

        <div style={styles.formHeader}>
          <h2 style={styles.formTitle}>Criar conta</h2>
          <p style={styles.formSubtitle}>Preencha os dados para começar</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="name">Nome</label>
            <div style={styles.inputWrapper}>
              <User
                size={18}
                style={{
                  ...styles.inputIcon,
                  color: focusedField === 'name' ? colors.accentPrimary : colors.textTertiary
                }}
              />
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Seu nome"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => setFocusedField('name')}
                onBlur={() => setFocusedField(null)}
                required
                autoComplete="name"
                style={{
                  ...styles.input,
                  ...(focusedField === 'name' ? styles.inputFocused : {}),
                }}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="email">E-mail</label>
            <div style={styles.inputWrapper}>
              <Mail
                size={18}
                style={{
                  ...styles.inputIcon,
                  color: focusedField === 'email' ? colors.accentPrimary : colors.textTertiary
                }}
              />
              <input
                type="email"
                id="email"
                name="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                required
                autoComplete="email"
                style={{
                  ...styles.input,
                  ...(focusedField === 'email' ? styles.inputFocused : {}),
                }}
              />
            </div>
          </div>

          <div style={styles.passwordRow}>
            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="password">Senha</label>
              <div style={styles.inputWrapper}>
                <Lock
                  size={18}
                  style={{
                    ...styles.inputIcon,
                    color: focusedField === 'password' ? colors.accentPrimary : colors.textTertiary
                  }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  style={{
                    ...styles.input,
                    paddingRight: '46px',
                    ...(focusedField === 'password' ? styles.inputFocused : {}),
                  }}
                />
                <button
                  type="button"
                  style={styles.inputAction}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="confirmPassword">Confirmar</label>
              <div style={styles.inputWrapper}>
                <Lock
                  size={18}
                  style={{
                    ...styles.inputIcon,
                    color: focusedField === 'confirmPassword' ? colors.accentPrimary : colors.textTertiary
                  }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('confirmPassword')}
                  onBlur={() => setFocusedField(null)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  style={{
                    ...styles.input,
                    ...(focusedField === 'confirmPassword' ? styles.inputFocused : {}),
                  }}
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            style={{
              ...styles.submitBtn,
              ...(loading ? styles.submitBtnDisabled : {}),
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Criando conta...
              </>
            ) : (
              <>
                Criar conta
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        <div style={styles.footer}>
          <p>
            Já tem uma conta?{' '}
            <Link to="/login" style={styles.link}>
              Fazer login
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        input::placeholder { color: ${colors.textTertiary}; }
      `}</style>
    </div>
  );
};

export default Register;
